import { useCallback, useEffect, useRef, useState } from 'react'
import { orderBy, uniqBy } from 'lodash'
import useSWR, { SWRResponse } from 'swr'

import { EnvironmentConfig } from '~/config'
import { xhrGetAsync, xhrGetBlobAsync, xhrPostAsync } from '~/libs/core'
import { handleError } from '~/libs/shared/lib/utils/handle-error'

import { AiFeedbackItem, Scorecard } from '../models'

export enum AiWorkflowRunStatusEnum {
    INIT = 'INIT',
    QUEUED = 'QUEUED',
    DISPATCHED = 'DISPATCHED',
    IN_PROGRESS = 'IN_PROGRESS',
    CANCELLED = 'CANCELLED',
    FAILURE = 'FAILURE',
    TIMEOUT = 'TIMEOUT',
    COMPLETED = 'COMPLETED',
    SUCCESS = 'SUCCESS',
}

export enum AiWorkflowReviewMethod {
    DETERMINISTIC = 'DETERMINISTIC',
    AI_ASSISTED = 'AI_ASSISTED'
}

export interface AiWorkflow {
    id: string;
    name: string;
    description: string;
    scorecard?: Scorecard
    defUrl: string
    reviewMethod?: string
    llm: {
        name: string
        description: string
        icon: string
        url: string
        provider: {
            name: string
        }
    }
}

export interface AiWorkflowRun {
    id: string;
    startedAt: string;
    lastDispatchedAt?: string;
    completedAt: string;
    status: AiWorkflowRunStatusEnum;
    gitRunId?: string;
    gitRunUrl?: string;
    initialScore?: number
    score: number;
    workflow: AiWorkflow
    usage: {
        input?: number
        output?: number
        inputTokens?: number
        outputTokens?: number
        cachedInputTokens?: number
        cacheCreationInputTokens?: number
    }
    commentsCount?: number
}

export interface AiWorkflowRunArtifact {
    id: number
    name: string
    size_in_bytes: number
    url: string
    archive_download_url: string
    expired: boolean
    workflow_run: {
        id: number
        repository_id: number
        head_sha: string
    }
    created_at: string
    updated_at: string
    expires_at: string
}

export type AiWorkflowRunItem = AiFeedbackItem

const TC_API_BASE_URL = EnvironmentConfig.API.V6

export const getAiWorkflowRunsCacheKey = (submissionId: string): string => (
    `${TC_API_BASE_URL}/workflows/runs?submissionId=${submissionId}`
)

export interface AiWorkflowRunsResponse {
    runs: AiWorkflowRun[]
    isLoading: boolean
}

export interface AiWorkflowRunItemsResponse {
    runItems: AiWorkflowRunItem[]
    isLoading: boolean
}

export interface AiWorkflowRunAttachmentsApiResponse {
    artifacts: AiWorkflowRunArtifact[]
    total_count: number
}

export interface AiWorkflowRunAttachmentsResponse {
    artifacts: AiWorkflowRunArtifact[]
    totalCount: number
    isLoading: boolean
}

export interface AiWorkflowRunArtifactDownloadResponse {
    download: (artifactId: number) => Promise<void>
    isDownloading: boolean
}

interface RetriggerAiWorkflowRunRequest {
    workflowRunId: string
}

interface QueueAiWorkflowRunsRequest {
    submissionId: string
    ignoreAiPhaseState?: boolean
}

export interface QueueAiWorkflowRunsResponse {
    queued: boolean
    runs: AiWorkflowRun[]
    message: string
}

export const aiRunInProgress = (aiRun: Pick<AiWorkflowRun, 'status'>): boolean => [
    AiWorkflowRunStatusEnum.INIT,
    AiWorkflowRunStatusEnum.QUEUED,
    AiWorkflowRunStatusEnum.DISPATCHED,
    AiWorkflowRunStatusEnum.IN_PROGRESS,
].includes(aiRun.status)

export const aiRunFailed = (aiRun: Pick<AiWorkflowRun, 'status'>): boolean => [
    AiWorkflowRunStatusEnum.FAILURE,
    AiWorkflowRunStatusEnum.CANCELLED,
    AiWorkflowRunStatusEnum.TIMEOUT,
].includes(aiRun.status)

/**
 * Keeps only the most recent run of each AI workflow.
 *
 * A re-run creates a new run that has been dispatched but not started yet, so its
 * `startedAt` is still empty. Its `lastDispatchedAt` is used instead, which makes the
 * new run win over the previous (possibly score-edited) run as soon as the re-run is
 * triggered. Used by `useFetchAiWorkflowsRuns` to feed the AI reviews table.
 *
 * @param runs - All runs returned for a submission, in any order.
 * @returns The latest run per workflow id.
 */
export function getLatestAiWorkflowRuns(runs: AiWorkflowRun[]): AiWorkflowRun[] {
    return uniqBy(
        orderBy(
            runs,
            [
                run => run.startedAt ?? run.lastDispatchedAt ?? '',
                run => run.completedAt ?? '',
            ],
            ['desc', 'desc'],
        ),
        'workflow.id',
    )
        .reverse()
}

export function useFetchAiWorkflowsRuns(
    submissionId: string,
): AiWorkflowRunsResponse {
    const hasInProgress = useRef(true)
    // Use swr hooks for challenge info fetching
    const {
        data: runs = [],
        error: fetchError,
        isValidating: isLoading,
    }: SWRResponse<AiWorkflowRun[], Error> = useSWR<AiWorkflowRun[], Error>(
        getAiWorkflowRunsCacheKey(submissionId),
        {
            isPaused: () => !submissionId,
            refreshInterval: hasInProgress.current ? 10 * 1000 : 0,
        },
    )

    hasInProgress.current = !!runs.find(r => (
        ![
            AiWorkflowRunStatusEnum.SUCCESS,
            AiWorkflowRunStatusEnum.CANCELLED,
            AiWorkflowRunStatusEnum.COMPLETED,
        ].includes(r.status)
    ))

    // Show backend error when fetching challenge info
    useEffect(() => {
        if (fetchError) {
            handleError(fetchError)
        }
    }, [fetchError])

    const uniqueRuns = getLatestAiWorkflowRuns(runs)

    return {
        isLoading,
        runs: uniqueRuns,
    }
}

export function useFetchAiWorkflowsRunItems(
    workflowId: string | undefined,
    runId: string | undefined,
    runStatus: string = '',
): AiWorkflowRunItemsResponse {
    // Use swr hooks for challenge info fetching
    const {
        data: runItems = [],
        error: fetchError,
        isValidating: isLoading,
    }: SWRResponse<AiWorkflowRunItem[], Error> = useSWR<AiWorkflowRunItem[], Error>(
        `${TC_API_BASE_URL}/workflows/${workflowId}/runs/${runId}/items?[${runStatus}]`,
        {
            fetcher: url => xhrGetAsync(url.replace(`[${runStatus}]`, '')),
            isPaused: () => !workflowId || !runId,
        },
    )

    // Show backend error when fetching challenge info
    useEffect(() => {
        if (fetchError) {
            handleError(fetchError)
        }
    }, [fetchError])

    return {
        isLoading,
        runItems,
    }
}

export function useFetchAiWorkflowsRunAttachments(
    workflowId?: string,
    runId?: string | undefined,
): AiWorkflowRunAttachmentsResponse {
    const {
        data,
        error: fetchError,
        isValidating: isLoading,
    }: SWRResponse<AiWorkflowRunAttachmentsApiResponse, Error> = useSWR<
        AiWorkflowRunAttachmentsApiResponse,
        Error
    >(
        `${TC_API_BASE_URL}/workflows/${workflowId}/runs/${runId}/attachments`,
        {
            isPaused: () => !workflowId || !runId,
        },
    )

    useEffect(() => {
        if (fetchError) {
            handleError(fetchError)
        }
    }, [fetchError])

    return {
        artifacts: data?.artifacts ?? [],
        isLoading,
        totalCount: data?.total_count ?? 0,
    }
}

export function useDownloadAiWorkflowsRunArtifact(
    workflowId?: string,
    runId?: string,
): AiWorkflowRunArtifactDownloadResponse {
    const [isDownloading, setIsDownloading] = useState(false)

    const download = useCallback(
        async (artifactId: number): Promise<void> => {
            if (!workflowId || !runId || !artifactId) return

            setIsDownloading(true)
            const url = `${TC_API_BASE_URL}/workflows/${workflowId}/runs/${runId}/attachments/${artifactId}/zip`

            try {
                const blob = await xhrGetBlobAsync<Blob>(url)

                const objectUrl = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = objectUrl
                link.download = `artifact-${artifactId}.zip`

                document.body.appendChild(link)
                link.click()
                link.remove()

                window.URL.revokeObjectURL(objectUrl)
            } catch (err) {
                handleError(err as Error)
            } finally {
                setIsDownloading(false)
            }
        },
        [workflowId, runId],
    )

    return {
        download,
        isDownloading,
    }
}

export const retriggerAiWorkflowRun = async (workflowRunId: string): Promise<AiWorkflowRun> => (
    xhrPostAsync<RetriggerAiWorkflowRunRequest, AiWorkflowRun>(
        `${TC_API_BASE_URL}/workflows/runs/retrigger`,
        { workflowRunId },
    )
)

/**
 * Manually queues the AI workflow runs configured for a submission.
 * Used to recover submissions whose runs were never queued because the
 * virus scan / AI phase opened event was missed.
 */
export const queueAiWorkflowRuns = async (
    submissionId: string,
    ignoreAiPhaseState?: boolean,
): Promise<QueueAiWorkflowRunsResponse> => (
    xhrPostAsync<QueueAiWorkflowRunsRequest, QueueAiWorkflowRunsResponse>(
        `${TC_API_BASE_URL}/workflows/runs/queue`,
        { ignoreAiPhaseState, submissionId },
    )
)

interface RebuildSubmissionDecisionRequest {
    submissionId: string
}

export interface RebuildSubmissionDecisionResponse {
    submissionId: string
    rebuilt: boolean
    message: string
}

export const rebuildSubmissionDecision = async (
    submissionId: string,
): Promise<RebuildSubmissionDecisionResponse> => (
    xhrPostAsync<RebuildSubmissionDecisionRequest, RebuildSubmissionDecisionResponse>(
        `${TC_API_BASE_URL}/workflows/runs/rebuild-decision`,
        { submissionId },
    )
)
